import React, { useState } from 'react';
import { 
  Card, 
  Space, 
  Tag, 
  Row, 
  Col, 
  Typography, 
  Avatar, 
  Descriptions,
  Divider,
  Button
} from 'antd';
import { 
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  DownOutlined,
  UpOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import PatientEditModal from './PatientEditModal';
import dayjs from 'dayjs';
import type { GetAppointmentQuery } from '../../generated/graphql';

const { Title, Text } = Typography;

type Patient = NonNullable<NonNullable<GetAppointmentQuery['appointment']>['app_user']>['patients'][0];

interface PatientInformationCardProps {
  patients?: Patient[];
  fallbackPhone?: string | null;
  fallbackEmail?: string | null;
  style?: React.CSSProperties;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onPatientUpdated?: (patient: Patient) => void;
}

// Helper function to calculate age from birthdate
const calculateAge = (birthdate: string): number => {
  if (!birthdate) return 0;
  const today = new Date();
  const birthDate = new Date(birthdate);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Helper function to get patient display name
const getPatientDisplayName = (patient: Patient, t: (k: string, v?: any) => string): string => {
  const englishName = `${patient.last_name_en || ''} ${patient.first_name_en || ''}`.trim();
  const chineseName = `${patient.last_name_zh_hk || ''}${patient.first_name_zh_hk || ''}`.trim();
  if (englishName && chineseName) return `${englishName} (${chineseName})`;
  if (englishName) return englishName;
  if (chineseName) return chineseName;
  return t('common.na');
};

// Helper function to get gender display text
const getGenderDisplay = (gender: string, t: (k: string) => string): string => {
  switch (gender) {
    case 'male': return t('patients.male');
    case 'female': return t('patients.female');
    case 'other': return t('patients.other');
    default: return t('common.na');
  }
};

// Helper function to get relationship display text
const getRelationshipDisplay = (relationship: string, t: (k: string) => string): string => {
  switch (relationship) {
    case 'self': return t('patients.relationships.self');
    case 'spouse': return t('patients.relationships.spouse');
    case 'child': return t('patients.relationships.child');
    case 'parent': return t('patients.relationships.parent');
    case 'sibling': return t('patients.relationships.sibling');
    case 'other': return t('patients.relationships.other');
    default: return t('common.na');
  }
};

const PatientInformationCard: React.FC<PatientInformationCardProps> = ({
  patients,
  fallbackPhone,
  fallbackEmail,
  style,
  collapsible = false,
  defaultCollapsed = false,
  onPatientUpdated
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  const toggleCollapse = () => {
    if (collapsible) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleEditClick = (patient: Patient) => {
    setEditingPatient(patient);
    setIsEditModalVisible(true);
  };

  const handleCancelEdit = () => {
    setIsEditModalVisible(false);
    setEditingPatient(null);
  };

  const handlePatientUpdated = (updatedPatient: Patient) => {
    onPatientUpdated?.(updatedPatient);
  };

  const renderPatientDetails = (patient: Patient, index: number, totalPatients: number) => (
    <div key={patient.id} style={{ marginBottom: index < totalPatients - 1 ? 24 : 0 }}>
      {index > 0 && <Divider />}
      
      {/* Patient Basic Info */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Space align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space align="start">
              <Avatar 
                src={patient.avatar_url || undefined} 
                icon={<UserOutlined />} 
                size={64} 
              />
              <div style={{ flex: 1 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {getPatientDisplayName(patient, t)}
                </Title>
                <Space wrap>
                  {patient.relationship_to_owner && (
                    <Tag color="blue">{getRelationshipDisplay(patient.relationship_to_owner, t)}</Tag>
                  )}
                  {patient.status && (
                    <Tag color={patient.status === 'active' ? 'green' : 'red'}>
                      {patient.status}
                    </Tag>
                  )}
                </Space>
              </div>
            </Space>
            <Button 
              type="dashed" 
              icon={<EditOutlined />} 
              onClick={() => handleEditClick(patient)}
              size="small"
            >
              {t('patients.editPatient')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Patient Details */}
      <Row gutter={[24, 16]}>
        {/* Personal Information */}
        <Col xs={24} md={12}>
          <Card size="small" title={t('patients.contact')} style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('patients.email')}>
                {patient.email || t('common.na')}
              </Descriptions.Item>
              <Descriptions.Item label={t('patients.phoneNumber')}>
                {patient.phone_number || t('common.na')}
              </Descriptions.Item>
              <Descriptions.Item label={t('patients.dateOfBirth')}>
                {patient.birthdate ? (
                  <Space>
                    <Text>{dayjs(patient.birthdate).format('YYYY-MM-DD')}</Text>
                    <Text type="secondary">({calculateAge(patient.birthdate) > 0 ? `${calculateAge(patient.birthdate)} ${t('patients.years')}` : '-'})</Text>
                  </Space>
                ) : t('common.na')}
              </Descriptions.Item>
              <Descriptions.Item label={t('patients.gender')}>
                {getGenderDisplay(patient.gender || '', t)}
              </Descriptions.Item>
              {patient.nationality && (
                <Descriptions.Item label={t('patients.nationality')}>
                  {patient.nationality}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>

        {/* Identity Information */}
        <Col xs={24} md={12}>
          <Card size="small" title={t('patients.identityInformation')} style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('patients.idDocumentType')}>
                {patient.identity_document_type || t('common.na')}
              </Descriptions.Item>
              <Descriptions.Item label={t('patients.idDocumentNumber')}>
                {patient.identity_document_number || t('common.na')}
              </Descriptions.Item>
              {patient.blood_type && (
                <Descriptions.Item label={t('patients.bloodType')}>
                  {patient.blood_type}
                </Descriptions.Item>
              )}
              {patient.language_preferences && patient.language_preferences.length > 0 && (
                <Descriptions.Item label={t('patients.languagePreferences')}>
                  <Space wrap>
                    {patient.language_preferences.map((lang, idx) => (
                      <Tag key={idx} color="blue">{lang}</Tag>
                    ))}
                  </Space>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* Emergency Contact */}
      {(patient.emergency_contact_name || patient.emergency_contact_phone) && (
        <Card size="small" title={t('patients.emergencyContact')} style={{ marginBottom: 16 }}>
          <Descriptions column={1} size="small">
            {patient.emergency_contact_name && (
              <Descriptions.Item label={t('patients.emergencyContactName')}>
                {patient.emergency_contact_name}
              </Descriptions.Item>
            )}
            {patient.emergency_contact_phone && (
              <Descriptions.Item label={t('patients.emergencyContactPhone')}>
                {patient.emergency_contact_phone}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      )}

      {/* Medical Information */}
      {(patient.allergies?.length || patient.chronic_conditions?.length || patient.medications?.length) && (
        <Card size="small" title={t('patients.medicalInfo')} style={{ marginBottom: 16 }}>
          {patient.allergies && patient.allergies.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t('patients.allergies')}:</Text>
              <br />
              <Space wrap>
                {patient.allergies.map((allergy, idx) => (
                  <Tag key={idx} color="red">{allergy}</Tag>
                ))}
              </Space>
            </div>
          )}
          
          {patient.chronic_conditions && patient.chronic_conditions.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t('patients.chronicConditions')}:</Text>
              <br />
              <Space wrap>
                {patient.chronic_conditions.map((condition, idx) => (
                  <Tag key={idx} color="orange">{condition}</Tag>
                ))}
              </Space>
            </div>
          )}
          
          {patient.medications && patient.medications.length > 0 && (
            <div>
              <Text strong>{t('patients.medications')}:</Text>
              <br />
              <Space wrap>
                {patient.medications.map((medication, idx) => (
                  <Tag key={idx} color="purple">{medication}</Tag>
                ))}
              </Space>
            </div>
          )}
        </Card>
      )}

      {/* Notes */}
      {patient.notes && (
        <Card size="small" title={t('patients.notes')}>
          <Text>{patient.notes}</Text>
        </Card>
      )}
    </div>
  );

  const renderFallbackPatientInfo = () => (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Space align="start">
          <Avatar icon={<UserOutlined />} size={64} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {fallbackPhone || t('common.na')}
            </Title>
            <Space>
              {fallbackPhone && (
                <Space>
                  <PhoneOutlined />
                  <Text>{fallbackPhone}</Text>
                </Space>
              )}
              {fallbackEmail && (
                <Space>
                  <MailOutlined />
                  <Text>{fallbackEmail}</Text>
                </Space>
              )}
            </Space>
          </div>
        </Space>
      </Col>
    </Row>
  );

  const renderCollapsedView = () => {
    if (patients && patients.length > 0) {
      const patient = patients[0]; // Show first patient in collapsed view
      return (
        <div style={{ padding: '8px 0' }}>
          <Space>
            <Avatar 
              src={patient.avatar_url || undefined} 
              icon={<UserOutlined />} 
              size={32} 
            />
            <div>
              <Text strong>
                {getPatientDisplayName(patient, t)}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {patient.relationship_to_owner && `${getRelationshipDisplay(patient.relationship_to_owner, t)} • `}
                {patient.birthdate && `${calculateAge(patient.birthdate)} ${t('patients.years')} • `}
                {patient.phone_number || fallbackPhone || t('common.na')}
              </Text>
            </div>
          </Space>
        </div>
      );
    } else {
      return (
        <div style={{ padding: '8px 0' }}>
          <Space>
            <Avatar icon={<UserOutlined />} size={32} />
            <div>
              <Text strong>
                {fallbackPhone || t('common.na')}
              </Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {fallbackEmail || t('common.na')}
              </Text>
            </div>
          </Space>
        </div>
      );
    }
  };

  return (
    <Card 
      title={
        <div 
          style={{ 
            cursor: collapsible ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
          onClick={toggleCollapse}
        >
          <span>{t('appointments.patientInformation')}</span>
          {collapsible && (
            <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {isCollapsed ? <DownOutlined /> : <UpOutlined />}
            </span>
          )}
        </div>
      }
      style={{ marginBottom: 24, ...style }}
    >
      {!isCollapsed && (
        <>
          {patients && patients.length > 0 ? (
            patients.map((patient, index) => renderPatientDetails(patient, index, patients.length))
          ) : (
            renderFallbackPatientInfo()
          )}
        </>
      )}
      {isCollapsed && renderCollapsedView()}

      {/* Patient Edit Modal */}
      <PatientEditModal
        visible={isEditModalVisible}
        patient={editingPatient}
        onCancel={handleCancelEdit}
        onSuccess={handlePatientUpdated}
      />
    </Card>
  );
};

export default PatientInformationCard;
